import s from "./ExternalAffiliateButton.module.css"
import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { usePostHog } from "posthog-js/react"
import autostoreIcon from "../assets/autostore-icon.png"
import servicePointsLogo from "../assets/servicepoints_logo_96x96.png"
import dollarIcon from "../assets/dollar-front-color.png"
import { useTranslation } from "react-i18next"
import { clsx } from "@mantine/core"
import DiamondIcon from "../assets/tourmaline.png"
import { useMediaQuery } from "../hooks/useMediaQuery.js"
const STORE_URL = "https://autostore.ai?utm_source=kopy"

function GridIcon({ className, "aria-hidden": ariaHidden = true, ...rest }) {
	return (
		<svg
			className={className}
			aria-hidden={ariaHidden}
			viewBox="0 0 24 24"
			fill="currentColor"
			focusable="false"
			width="1em"
			height="1em"
			{...rest}
		>
			<path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
		</svg>
	)
}

const ExternalAffiliateButtonRaw = ({ isSecondRow = false }) => {
	const posthog = usePostHog()
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const triggerRef = useRef(null)
	const menuRef = useRef(null)
	const isHoverable = useMemo(() => {
		if (typeof window === "undefined" || !window.matchMedia) return false
		return window.matchMedia("(hover: hover) and (pointer: fine)").matches
	}, [])
	const isMobile = useMediaQuery("(max-width: 640px)")

	// delay closing on hover-leave to allow moving the cursor from the trigger to the menu without flicker
	const hoverCloseTimeout = useRef(null)
	const openByHover = useCallback(() => {
		if (!isHoverable) return
		if (hoverCloseTimeout.current) {
			clearTimeout(hoverCloseTimeout.current)
			hoverCloseTimeout.current = null
		}
		setOpen(true)
	}, [isHoverable])
	const scheduleHoverClose = useCallback(() => {
		if (!isHoverable) return
		if (hoverCloseTimeout.current) clearTimeout(hoverCloseTimeout.current)
		hoverCloseTimeout.current = setTimeout(() => {
			setOpen(false)
			hoverCloseTimeout.current = null
		}, 160)
	}, [isHoverable])

	const handlePrimaryClicked = useCallback(() => {
		posthog?.capture("autostore_affiliate_link_clicked")
	}, [posthog])

	const closeMenu = useCallback(() => setOpen(false), [])

	useEffect(() => {
		if (!open) return
		function onKeyDown(e) {
			if (e.key === "Escape") {
				setOpen(false)
				triggerRef.current?.focus()
			}
		}
		function onClickOutside(e) {
			if (!menuRef.current) return
			if (
				menuRef.current.contains(e.target) ||
				triggerRef.current?.contains(e.target)
			)
				return
			setOpen(false)
		}
		document.addEventListener("keydown", onKeyDown)
		document.addEventListener("mousedown", onClickOutside)
		return () => {
			document.removeEventListener("keydown", onKeyDown)
			document.removeEventListener("mousedown", onClickOutside)
			if (hoverCloseTimeout.current) {
				clearTimeout(hoverCloseTimeout.current)
				hoverCloseTimeout.current = null
			}
		}
	}, [open])

	return (
		<div
			className={s.container}
			role="group"
			aria-label={t("externalAffiliate.groupLabel")}
			style={{
				marginLeft: "auto",
				marginTop: isSecondRow ? "3rem" : "0",
				flexDirection: isSecondRow && !isMobile ? "column" : "row",
			}}
		>
			<a
				href={STORE_URL}
				className={s.primary}
				target="_blank"
				rel="noopener noreferrer"
				onClick={handlePrimaryClicked}
				aria-label={t("externalAffiliate.primaryAria")}
			>
				<img
					src={autostoreIcon}
					alt={"autostore.ai logo"}
					className={s.icon}
				/>
				<span className={s.primaryText}>
					<span className="visually-hidden">
						{t("externalAffiliate.primaryHiddenPrefix")}
					</span>
					{t("externalAffiliate.primaryText")}
				</span>
			</a>

			{(!isSecondRow || isMobile) && (
				<div className={s.divider} aria-hidden="true" />
			)}

			<a
				href={
					"https://www.searchthetrend.com/?utm_source=kopy&utm_medium=referral&utm_campaign=cross_promo"
				}
				className={s.primary}
				target="_blank"
				rel="noopener noreferrer"
			>
				<img src={DiamondIcon} className={s.icon} />
				<span className={s.primaryText}>
					{t("externalAffiliate.find_winning_products")}
				</span>
			</a>

			{!isSecondRow && <div className={s.divider} aria-hidden="true" />}

			<button
				ref={triggerRef}
				type="button"
				className={s.moreApps}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				onMouseEnter={openByHover}
				onMouseLeave={scheduleHoverClose}
			>
				<span>{t("externalAffiliate.moreApps")}</span>
				<GridIcon
					className={s.icon}
					style={{ width: "16px", height: "16px" }}
				/>
			</button>

			{open && (
				<ul
					ref={menuRef}
					className={s.menu}
					role="menu"
					aria-label={t("externalAffiliate.menuAria")}
					onMouseEnter={openByHover}
					onMouseLeave={scheduleHoverClose}
				>
					<li role="none">
						<a
							role="menuitem"
							href="https://apps.shopify.com/m8trics-profit-analytics"
							target="_blank"
							rel="noopener noreferrer"
							className={s.menuItem}
							onClick={closeMenu}
						>
							<svg
								width="25"
								height="25"
								viewBox="0 0 25 25"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className={s.menuIcon}
							>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M6.62305 0H18.2276C22.4537 0 24.8395 2.33194 24.8519 6.44333V17.7343C24.8519 21.8445 22.4537 24.1777 18.2276 24.1777H6.62305C2.39696 24.1777 0 21.8445 0 17.7343V6.44333C0 2.33194 2.39696 0 6.62305 0ZM12.4867 19.1728C13.0223 19.1728 13.4684 18.7859 13.5181 18.2661V5.94759C13.5678 5.57282 13.3826 5.19687 13.0471 4.9926C12.698 4.78707 12.2755 4.78707 11.9412 4.9926C11.6045 5.19687 11.4193 5.57282 11.4554 5.94759V18.2661C11.5188 18.7859 11.9648 19.1728 12.4867 19.1728ZM18.2039 19.1729C18.7258 19.1729 19.1719 18.786 19.2353 18.2662V14.301C19.2713 13.913 19.0862 13.5515 18.7494 13.346C18.4151 13.1405 17.9927 13.1405 17.6447 13.346C17.308 13.5515 17.1228 13.913 17.1725 14.301V18.2662C17.2222 18.786 17.6683 19.1729 18.2039 19.1729ZM7.72766 18.2662C7.67797 18.786 7.23186 19.1729 6.69634 19.1729C6.16197 19.1729 5.71464 18.786 5.66617 18.2662V9.91276C5.62895 9.5368 5.8141 9.16328 6.15085 8.95776C6.48508 8.75224 6.90881 8.75224 7.24427 8.95776C7.57858 9.16328 7.76617 9.5368 7.72766 9.91276V18.2662Z"
									fill="#65CDDC"
								/>
							</svg>

							<span>{t("externalAffiliate.itemProfit")}</span>
						</a>
					</li>
					<li role="none">
						<a
							role="menuitem"
							href="https://www.servicepoints.eu/en/t6/kopy"
							target="_blank"
							rel="noopener noreferrer"
							className={s.menuItem}
							onClick={closeMenu}
						>
							<img
								src={servicePointsLogo}
								alt={"ServicePoints Logo"}
								className={s.menuIcon}
							/>
							<span>
								{t("externalAffiliate.itemServicePoints")}
							</span>
						</a>
					</li>
					<li role="none">
						<a
							role="menuitem"
							href="https://affiliates.kopy.app/login"
							target="_blank"
							rel="noopener noreferrer"
							className={s.menuItem}
							onClick={closeMenu}
						>
							<div>
								<div
									className={clsx(
										s.affiliateShine,
										s.menuIcon,
									)}
									style={{
										background: "#ffe3ab",
										borderRadius: "0.5rem",
									}}
								>
									<div
										style={{
											cursor: "pointer",
											verticalAlign: "middle",
											height: "16px",
											display: "flex",
											gap: "0.2rem",
											alignItems: "center",
										}}
									>
										<img
											alt={"Kopy Affiliate Icon"}
											style={{
												height: "16px",
												width: "16px",
												filter: "brightness(1.25) saturate(1.5) drop-shadow(-2px 1px 2px rgba(47, 47, 47, 0.25))",
											}}
											src={dollarIcon}
										/>
									</div>
								</div>
							</div>

							<span>
								{t("externalAffiliate.itemKopyAffiliate")}
							</span>
						</a>
					</li>
				</ul>
			)}
		</div>
	)
}

export const ExternalAffiliateButton = memo(ExternalAffiliateButtonRaw)
